import React, { useEffect } from 'react';
import SectionHead from '../components/ui/SectionHead.jsx';
import Sidebar from '../components/ui/Sidebar.jsx';

const PROCESS_STEPS = [
  { step: 1, label: 'Initial screening by editorial office', timeframe: '48 hours' },
  { step: 2, label: 'Assignment to section editor', timeframe: '' },
  { step: 3, label: 'At least two independent reviewer assignments', timeframe: '' },
  { step: 4, label: 'Review completion', timeframe: '2–4 weeks speedy peer review' },
  { step: 5, label: 'Editor decision: Accept / Major Revision / Minor Revision / Reject', timeframe: '' },
  { step: 6, label: 'Author response and resubmission', timeframe: '' },
  { step: 7, label: 'Final editorial decision', timeframe: '' },
];

const sidebarLinks = [
  { to: '/author-guidelines', label: 'Author Guidelines' },
  { to: '/aims-and-scope', label: 'Aims & Scope' },
  { to: '/editorial-board', label: 'Editorial Board' },
  { to: '/contact', label: 'Contact Us' },
];

function PeerReviewPolicy() {
  useEffect(() => {
    document.title = 'Peer Review Policy — JAMSD';
  }, []);

  return (
    <>
      {/* Page hero */}
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 items-start">
            <div className="w-1 bg-amber rounded-full self-stretch" aria-hidden="true" />
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">
                Editorial Policy
              </p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">
                Peer Review Policy
              </h1>
              <p className="font-sans text-slate-light text-base max-w-2xl">
                JAMSD upholds rigorous, fair, and transparent peer review to ensure the quality and
                integrity of published research.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Overview" />
              <p className="font-sans text-gray-700 leading-relaxed">
                All submissions undergo a <strong>double-blind peer review process</strong> by at least two
                independent experts. Editorial decisions are made based on scientific merit, methodological
                rigor, relevance, and ethical compliance.
              </p>
            </div>

            {/* Review Model */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Review Model" />
              <div className="flex items-start gap-4 p-4 bg-amber-tint rounded-lg border border-amber">
                <svg className="w-8 h-8 text-amber flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <h3 className="font-display text-navy font-bold text-lg mb-2">Double-Blind Peer Review</h3>
                  <p className="font-sans text-amber-tinttext text-sm leading-relaxed">
                    The identities of both authors and reviewers are concealed throughout the entire review
                    process. This model eliminates bias based on authorship, institutional affiliation,
                    or reviewer identity.
                  </p>
                </div>
              </div>
            </div>

            {/* Process */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Review Process" />
              <div className="space-y-4">
                {PROCESS_STEPS.map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-navy text-white font-sans font-bold text-sm flex items-center justify-center">
                      {item.step}
                    </div>
                    <div className="flex-1 border-b border-bg-border pb-4">
                      <p className="font-sans text-gray-700 text-sm leading-relaxed">{item.label}</p>
                      {item.timeframe && (
                        <span className="inline-block mt-1 text-xs font-sans text-amber font-semibold bg-amber-tint px-2 py-0.5 rounded">
                          {item.timeframe}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reviewer Responsibilities */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Reviewer Responsibilities" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed">
                <p className="mb-4">Reviewers must:</p>
                <ul className="space-y-3">
                  {[
                    'Disclose any conflicts of interest before agreeing to review',
                    'Maintain strict confidentiality of the manuscript and its contents',
                    'Provide constructive, specific, and objective feedback',
                    'Complete reviews within agreed timelines (2–4 weeks)',
                    'Flag any potential ethical concerns or suspected misconduct',
                    'Decline to review manuscripts with which they have a conflict',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-amber flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Editorial Decision */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Editorial Decision" />
              <p className="font-sans text-gray-700 text-sm leading-relaxed mb-5">
                Final decisions are made by the handling editor in consultation with the editorial board,
                based on reviewers' recommendations. Possible decisions include:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Accept', color: 'bg-green-100 text-green-800 border-green-200' },
                  { label: 'Minor Revision', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                  { label: 'Major Revision', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                  { label: 'Reject', color: 'bg-red-100 text-red-800 border-red-200' },
                ].map((d) => (
                  <div
                    key={d.label}
                    className={`border rounded-lg p-3 text-center font-sans text-sm font-semibold ${d.color}`}
                  >
                    {d.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Publication Ethics */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Publication Ethics" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  JAMSD follows <strong>COPE (Committee on Publication Ethics) guidelines</strong> and the{' '}
                  <strong>Declaration of Helsinki</strong> for studies involving human subjects.
                </p>
                <p>
                  Cases of suspected misconduct — including plagiarism, data fabrication, duplicate
                  submission, or authorship disputes — will be investigated according to COPE flowcharts
                  and recommendations.
                </p>
                <p>
                  Published articles may be retracted or corrected if post-publication issues are identified.
                </p>
              </div>
            </div>

            {/* Interested in Reviewing */}
            <div className="bg-bg-ice rounded-lg border border-bg-border p-6 text-center">
              <h3 className="font-display text-navy text-xl font-bold mb-2">
                Interested in Reviewing for JAMSD?
              </h3>
              <p className="font-sans text-gray-600 text-sm mb-4 max-w-lg mx-auto">
                We are building our reviewer pool. If you are an expert in any of our target disciplines
                and would like to contribute as a peer reviewer, contact our editorial office.
              </p>
              <a href="mailto:editor@jamsd.org" className="btn-amber inline-flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Editorial Office
              </a>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-72 xl:w-80 flex-shrink-0">
            <Sidebar quickLinks={sidebarLinks} showSubmitCta />
          </div>
        </div>
      </div>
    </>
  );
}

export default PeerReviewPolicy;
